<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FlowFilters from './FlowFilters.vue';
import FlowRow from './FlowRow.vue';

const router = useRouter();
const { accountId } = useAccount();
const { t } = useI18n();

const flows = ref([]);
const isLoading = ref(true);
const isCreating = ref(false);
const deletingId = ref(null);
const collapsedFolders = ref(new Set());

const search = ref('');
const channelFilter = ref('');
const statusFilter = ref('');
const folderFilter = ref('');

const NO_FOLDER_KEY = '__none__';

const loadFlows = async () => {
  isLoading.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.get();
    flows.value = data?.payload ?? [];
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.LOAD_ERROR'));
    flows.value = [];
  } finally {
    isLoading.value = false;
  }
};

const availableChannels = computed(() =>
  [...new Set(flows.value.flatMap(flow => flow.channels ?? []))].sort()
);

const availableFolders = computed(() =>
  [...new Set(flows.value.map(flow => flow.folder).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b, 'pt-BR')
  )
);

const filteredFlows = computed(() => {
  const term = search.value.trim().toLowerCase();
  return flows.value.filter(flow => {
    if (term && !flow.name?.toLowerCase().includes(term)) return false;
    if (
      channelFilter.value &&
      !(flow.channels ?? []).includes(channelFilter.value)
    )
      return false;
    if (statusFilter.value && flow.health !== statusFilter.value) return false;
    if (folderFilter.value && flow.folder !== folderFilter.value) return false;
    return true;
  });
});

// Pastas em ordem alfabética; "Sem pasta" sempre por último, nunca escondido.
const groups = computed(() => {
  const byFolder = new Map();
  filteredFlows.value.forEach(flow => {
    const key = flow.folder || NO_FOLDER_KEY;
    if (!byFolder.has(key)) byFolder.set(key, []);
    byFolder.get(key).push(flow);
  });

  const named = [...byFolder.entries()]
    .filter(([key]) => key !== NO_FOLDER_KEY)
    .sort(([a], [b]) => a.localeCompare(b, 'pt-BR'))
    .map(([key, items]) => ({ key, label: key, items }));

  const loose = byFolder.get(NO_FOLDER_KEY);
  return loose
    ? [
        ...named,
        {
          key: NO_FOLDER_KEY,
          label: t('FLOW_AUTOMATIONS.INDEX.NO_FOLDER'),
          items: loose,
        },
      ]
    : named;
});

const attentionCount = computed(
  () =>
    flows.value.filter(flow => ['stalled', 'never_ran'].includes(flow.health))
      .length
);

const toggleFolder = key => {
  const next = new Set(collapsedFolders.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  collapsedFolders.value = next;
};

const openEditor = flow => {
  router.push({
    name: 'conversation_workflows_edit',
    params: { accountId: accountId.value, workflowId: String(flow.id) },
  });
};

const createFlow = async () => {
  isCreating.value = true;
  try {
    const { data } = await conversationWorkflowsAPI.create({
      conversation_workflow: {
        name: t('FLOW_AUTOMATIONS.INDEX.DEFAULT_NAME'),
        description: '',
        active: true,
        graph: { nodes: [], edges: [] },
      },
    });
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_SUCCESS'));
    const id = data?.payload?.id;
    if (id) openEditor({ id });
    else await loadFlows();
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const deleteFlow = async flow => {
  if (!window.confirm(t('FLOW_AUTOMATIONS.INDEX.DELETE_CONFIRM'))) return;
  deletingId.value = flow.id;
  try {
    await conversationWorkflowsAPI.delete(flow.id);
    useAlert(t('FLOW_AUTOMATIONS.INDEX.DELETE_SUCCESS'));
    await loadFlows();
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.DELETE_ERROR'));
  } finally {
    deletingId.value = null;
  }
};

const moveFlow = async (flow, rawFolder) => {
  let folder = rawFolder;
  if (folder === '__new__') {
    folder = window
      .prompt(t('FLOW_AUTOMATIONS.INDEX.NEW_FOLDER_PROMPT'))
      ?.trim();
    if (!folder) return;
  }
  if ((flow.folder ?? '') === folder) return;

  try {
    await conversationWorkflowsAPI.update(flow.id, {
      conversation_workflow: { folder },
    });
    useAlert(t('FLOW_AUTOMATIONS.INDEX.MOVE_SUCCESS'));
    await loadFlows();
  } catch {
    useAlert(t('FLOW_AUTOMATIONS.INDEX.MOVE_ERROR'));
  }
};

onMounted(loadFlows);
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-6">
    <template #header>
      <div
        class="flex flex-col gap-4 w-full md:flex-row md:items-start md:justify-between"
      >
        <BaseSettingsHeader
          :title="$t('FLOW_AUTOMATIONS.INDEX.HEADER.TITLE')"
          :description="$t('FLOW_AUTOMATIONS.INDEX.HEADER.DESCRIPTION')"
          feature-name="conversation-workflows"
        />
        <Button
          :label="$t('FLOW_AUTOMATIONS.INDEX.NEW')"
          icon="i-ri-add-line"
          :is-loading="isCreating"
          @click="createFlow"
        />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <FlowFilters
          v-if="flows.length"
          v-model:search="search"
          v-model:channel="channelFilter"
          v-model:status="statusFilter"
          v-model:folder="folderFilter"
          :available-channels="availableChannels"
          :available-folders="availableFolders"
        />

        <button
          v-if="attentionCount && !statusFilter"
          type="button"
          class="flex items-center gap-2 rounded-xl border border-n-amber-6 bg-n-amber-2 px-4 py-2 text-sm text-n-amber-11 text-left"
          @click="statusFilter = 'stalled'"
        >
          <span aria-hidden="true">{{ '⚠' }}</span>
          <span>{{
            $t('FLOW_AUTOMATIONS.INDEX.ATTENTION_BANNER', {
              count: attentionCount,
            })
          }}</span>
        </button>

        <div v-if="isLoading" class="flex justify-center py-12 text-n-slate-11">
          {{ $t('FLOW_AUTOMATIONS.INDEX.LOADING') }}
        </div>

        <div
          v-else-if="!flows.length"
          class="rounded-xl border border-n-weak bg-n-alpha-1 px-6 py-12 text-center text-n-slate-11"
        >
          {{ $t('FLOW_AUTOMATIONS.INDEX.EMPTY') }}
        </div>

        <div
          v-else-if="!filteredFlows.length"
          class="rounded-xl border border-n-weak bg-n-alpha-1 px-6 py-12 text-center text-n-slate-11"
        >
          {{ $t('FLOW_AUTOMATIONS.INDEX.EMPTY_FILTERED') }}
        </div>

        <template v-else>
          <div v-for="group in groups" :key="group.key" class="flex flex-col">
            <button
              type="button"
              class="flex items-center gap-2 px-1 py-2 text-sm font-medium text-n-slate-11 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9 rounded-md"
              :aria-expanded="!collapsedFolders.has(group.key)"
              @click="toggleFolder(group.key)"
            >
              <span
                aria-hidden="true"
                class="transition-transform"
                :class="collapsedFolders.has(group.key) ? '' : 'rotate-90'"
                >{{ '›' }}</span
              >
              <span>{{ group.label }}</span>
              <span class="text-n-slate-10">({{ group.items.length }})</span>
            </button>

            <div
              v-if="!collapsedFolders.has(group.key)"
              class="overflow-hidden rounded-xl border border-n-weak divide-y divide-n-weak"
            >
              <FlowRow
                v-for="flow in group.items"
                :key="flow.id"
                :flow="flow"
                :folders="availableFolders"
                :is-deleting="deletingId === flow.id"
                @open="openEditor"
                @delete="deleteFlow"
                @move="moveFlow"
              />
            </div>
          </div>
        </template>
      </div>
    </template>
  </SettingsLayout>
</template>
